import React, { useState, useEffect, useMemo } from "react";
import { Badge, Card, Collapse, Row, Col, Table, Form, InputGroup, Spinner, Pagination, Button } from "react-bootstrap";
import { BsBookmarkPlusFill } from "react-icons/bs";
import { FaChevronLeft, FaChevronRight, FaFilter, FaTimes } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Select from "react-select";

import { useAuth } from "../../context/AuthContext";
import { useTickets } from "../../hooks/TICKET/useGetTickets";
import { getTickets } from "../../api/TICKET/ticket";
import { useClients } from "../../hooks/useGetClients";
import { useTicketTypes } from "../../hooks/TICKET/useTicketTypes";
import { useTicketPriorities } from "../../hooks/TICKET/useTicketPriorities";
import { useFacturationModes } from "../../hooks/TICKET/useFacturationModes";
import { useUsers } from "../../hooks/useGetUsers";
import { useGroupes } from "../../hooks/useGetGroupes";
import { customSelectStyles } from "../../utils/reactSelectStyles";
import TicketRow from "./TicketRow";
import NewTicket from "../../components/Modals/Create/NewTicket";

// ---------------------------------------------------------------------------
// Breakpoint below which the card layout is used instead of the table.
// ---------------------------------------------------------------------------
const MOBILE_BREAKPOINT = 1000;

const ACTIVE_FILTER_STATUSES = ["open", "in_progress"];

const STATUS_LABELS = {
  open:        "Ouvert",
  in_progress: "En cours",
};

const STATUS_BADGE = { open: "danger", in_progress: "warning" };

const OP_OPTIONS = [
  { value: "=",  label: "=" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: ">",  label: ">" },
  { value: "<",  label: "<" },
];

const translateStatus = (status) => STATUS_LABELS[status] ?? "Non assigné";

const Tickets = ({ titre }) => {
  // ── Router ────────────────────────────────────────────────────────────────
  const location       = useLocation();
  const restoredState  = location.state; // state passed back from ticket detail

  // ── Auth ──────────────────────────────────────────────────────────────────
  const { hasPermission } = useAuth();

  // ── Local UI state ────────────────────────────────────────────────────────
  const [search,          setSearch]          = useState(restoredState?.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(restoredState?.search || "");
  const [page,            setPage]            = useState(restoredState?.page   || 1);
  const [limit,           setLimit]           = useState(restoredState?.limit  || 10);
  const [showNewTicket,   setShowNewTicket]   = useState(false);
  const [isMobile,        setIsMobile]        = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [showFilters,     setShowFilters]     = useState(restoredState?.showFilters ?? false);
  const [sortField,       setSortField]       = useState("date_debut");
  const [sortOrder,       setSortOrder]       = useState("DESC");

  // ── Filter state ──────────────────────────────────────────────────────────
  const [filterStatuses,    setFilterStatuses]    = useState(
    restoredState?.filterStatuses ? new Set(restoredState.filterStatuses) : new Set(ACTIVE_FILTER_STATUSES)
  );
  const [filterClient,      setFilterClient]      = useState(restoredState?.filterClient      ?? null);
  const [filterClientInput, setFilterClientInput] = useState(restoredState?.filterClientInput ?? "");
  const [filterDescription, setFilterDescription] = useState(restoredState?.filterDescription ?? "");
  const [debouncedDesc,     setDebouncedDesc]     = useState(restoredState?.filterDescription ?? "");
  const [filterType,        setFilterType]        = useState(restoredState?.filterType        ?? null);
  const [filterPriorite,    setFilterPriorite]    = useState(restoredState?.filterPriorite    ?? null);
  const [filterFacturation, setFilterFacturation] = useState(restoredState?.filterFacturation ?? []);
  const [filterAssignedUsers,  setFilterAssignedUsers]  = useState(restoredState?.filterAssignedUsers  ?? []);
  const [filterAssignedGroups, setFilterAssignedGroups] = useState(restoredState?.filterAssignedGroups ?? []);
  const [filterDateDebut,   setFilterDateDebut]   = useState(restoredState?.filterDateDebut   ?? { op: ">=", val: "" });
  const [filterDateFin,     setFilterDateFin]     = useState(restoredState?.filterDateFin     ?? { op: "<=", val: "" });
  const [filterDateCloture, setFilterDateCloture] = useState(restoredState?.filterDateCloture ?? { op: "=",  val: "" });

  // ── Options for selects ───────────────────────────────────────────────────
  const { data: clientsData }   = useClients(1, 20, filterClientInput.length >= 3 ? filterClientInput : "");
  const clientOptions           = (clientsData?.data?.clients ?? []).map(c => ({ value: c.id, label: c.name }));
  const { data: typesData }     = useTicketTypes();
  const typeOptions             = (typesData?.data ?? []).map(t => ({ value: t.id, label: t.label }));
  const { data: prioritesData } = useTicketPriorities();
  const prioriteOptions         = (prioritesData?.data ?? []).map(p => ({ value: p.id, label: p.label }));
  const { data: factModesData } = useFacturationModes();
  const factOptions             = (factModesData?.data ?? []).map(f => ({ value: f.id, label: f.label }));
  const { data: usersData }     = useUsers();
  const userOptions             = (usersData?.data ?? []).map(u => ({ value: u.id, label: `${u.name ?? ''} ${u.surname ?? ''}`.trim() }));
  const { data: groupesData }   = useGroupes();
  const groupOptions            = (groupesData?.data ?? []).map(g => ({ value: g.id, label: g.name }));

  // ── API filters ───────────────────────────────────────────────────────────
  const apiFilters = useMemo(() => ({
    statuses:    [...filterStatuses],
    clientId:    filterClient?.value    ?? null,
    description: debouncedDesc,
    type:        filterType?.value        ?? null,
    priorite:    filterPriorite?.value    ?? null,
    facturationIds: filterFacturation.map(f => f.value),
    userIds:     filterAssignedUsers.map(u => u.value),
    groupIds:    filterAssignedGroups.map(g => g.value),
    dateDebut:   filterDateDebut.val  ? filterDateDebut  : null,
    dateFin:     filterDateFin.val    ? filterDateFin    : null,
    dateCloture: filterDateCloture.val ? filterDateCloture : null,
  }), [filterStatuses, filterClient, debouncedDesc, filterType, filterPriorite,
      filterFacturation, filterAssignedUsers, filterAssignedGroups, filterDateDebut, filterDateFin, filterDateCloture]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterStatuses.size !== ACTIVE_FILTER_STATUSES.length || ACTIVE_FILTER_STATUSES.some(s => !filterStatuses.has(s))) n++;
    if (filterClient)          n++;
    if (debouncedDesc)         n++;
    if (filterType)            n++;
    if (filterPriorite)        n++;
    if (filterFacturation.length > 0) n++;
    if (filterAssignedUsers.length > 0) n++;
    if (filterAssignedGroups.length > 0) n++;
    if (filterDateDebut.val)   n++;
    if (filterDateFin.val)     n++;
    if (filterDateCloture.val) n++;
    return n;
  }, [filterStatuses, filterClient, debouncedDesc, filterType, filterPriorite,
      filterFacturation, filterAssignedUsers, filterAssignedGroups, filterDateDebut, filterDateFin, filterDateCloture]);

  // ── Remote data ───────────────────────────────────────────────────────────
  const queryClient                  = useQueryClient();
  const { data, isLoading, isError } = useTickets(page, limit, debouncedSearch, apiFilters, sortField, sortOrder);

  // ── Derived values ────────────────────────────────────────────────────────
  const tickets    = Array.isArray(data?.data?.tickets) ? data.data.tickets : [];
  const totalItems = data?.data?.count || 0;
  const totalPages = Math.ceil(totalItems / limit);

  const formattedTickets = tickets.map(t => ({
    id:            t.id,
    dateDebut:     t.start_date || t.created_at,
    client:        t.client?.name || "Non assigné",
    clientId:      t.client?.id,
    titre:         t.title,
    intervenants: [
  ...(t.groups?.map(g => g.name) || []),
  ...(t.users?.map(u => `${u.name} ${u.surname}`) || [])
  ],
    statut:        translateStatus(t.status),
    rawStatus:     t.status,
    interventions: t.ticket_interventions || [],
  }));

  // ── Effects ───────────────────────────────────────────────────────────────

  // Listen to window resize to switch between card and table layout.
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce: attend 500ms après la dernière frappe avant de déclencher la requête.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Debounce filter description.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDesc(filterDescription), 500);
    return () => clearTimeout(timer);
  }, [filterDescription]);

  // Prefetch the next page so navigation feels instant.
  useEffect(() => {
    if (page < totalPages) {
      queryClient.prefetchQuery({
        queryKey: ["tickets", page + 1, limit, debouncedSearch, apiFilters, sortField, sortOrder],
        queryFn:  () => getTickets(page + 1, limit, debouncedSearch, apiFilters, sortField, sortOrder),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [page, limit, debouncedSearch, apiFilters, sortField, sortOrder, totalPages, queryClient]);

  // Restore scroll position when the user navigates back from a ticket detail.
  useEffect(() => {
    if (restoredState?.scrollY && !isLoading) {
      window.scrollTo(0, restoredState.scrollY);
    }
  }, [restoredState, isLoading]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value.toString());
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  };

  const toggleStatus = (s) => {
    setFilterStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilterStatuses(new Set(ACTIVE_FILTER_STATUSES));
    setFilterClient(null);
    setFilterClientInput("");
    setFilterDescription("");
    setDebouncedDesc("");
    setFilterType(null);
    setFilterPriorite(null);
    setFilterFacturation([]);
    setFilterAssignedUsers([]);
    setFilterAssignedGroups([]);
    setFilterDateDebut({ op: ">=", val: "" });
    setFilterDateFin({ op: "<=", val: "" });
    setFilterDateCloture({ op: "=",  val: "" });
    setPage(1);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  // Build a compact page-number array with ellipsis for large page counts.
  const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const delta = 2;
    const left  = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    return [
      1,
      ...(left > 2 ? ["..."] : []),
      ...Array.from({ length: right - left + 1 }, (_, i) => left + i),
      ...(right < total - 1 ? ["..."] : []),
      total,
    ];
  };

  // ── Sub-components ────────────────────────────────────────────────────────

  // Reusable pagination bar — rendered at the top and bottom of the list.
  const paginationBar = (keyPrefix) => (
    <Pagination className="mb-0 flex-wrap">
      <Pagination.Prev
        onClick={() => setPage(p => Math.max(1, p - 1))}
        disabled={page === 1}
      >
        {isMobile ? <FaChevronLeft /> : "Précédent"}
      </Pagination.Prev>

      {getPageNumbers(page, totalPages).map((p, idx) =>
        p === "..." ? (
          <Pagination.Ellipsis key={`${keyPrefix}-ellipsis-${idx}`} disabled />
        ) : (
          <Pagination.Item
            key={`${keyPrefix}-page-${p}`}
            active={p === page}
            onClick={() => setPage(p)}
          >
            {p}
          </Pagination.Item>
        )
      )}

      <Pagination.Next
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        disabled={page === totalPages}
      >
        {isMobile ? <FaChevronRight /> : "Suivant"}
      </Pagination.Next>
    </Pagination>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Page header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">{titre}</h1>

        {hasPermission("CREATE_TICKET") && (
          <button
            className="btn btn-warning btn-icon-split ms-2 mb-0"
            onClick={() => setShowNewTicket(true)}
          >
            <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
              <BsBookmarkPlusFill style={{ color: "#fff" }} />
            </span>
            <span className="text">Ajouter un ticket</span>
          </button>
        )}
      </div>

      {/* New-ticket modal — monté uniquement quand ouvert */}
      {showNewTicket && <NewTicket showNewTicket={showNewTicket} setShowNewTicket={setShowNewTicket} />}

      <div className="row">
        <div className="col-12">
          <Card className="border-left-warning shadow mb-4">
            <Card.Body>

              {/* Toolbar: limit selector + search + top pagination */}
              <div
                className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3"
                style={{ width: "100%" }}
              >
                <div style={{ flex: "1 1 150px", maxWidth: "70px" }}>
                  <Form.Select value={limit} onChange={handleLimitChange} disabled={isLoading}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </div>

                <div style={{ flex: "2 1 200px", maxWidth: "350px" }}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Recherche par titre"
                      value={search}
                      onChange={handleSearchChange}
                      className="before-input"
                    />
                  </InputGroup>
                </div>

                {/* Filter toggle button */}
                <div>
                  <Button
                    variant={showFilters || activeFilterCount > 0 ? "warning" : "outline-secondary"}
                    className="d-flex align-items-center gap-2 filter-button"
                    onClick={() => setShowFilters(prev => !prev)}
                  >
                    <FaFilter />
                    <span>Filtres</span>
                    {activeFilterCount > 0 && (
                      <Badge bg="light" text="dark" pill className="ms-1" style={{ display: 'inline-flex', alignItems: 'center', height: 'fit-content' }}>{activeFilterCount}</Badge>
                    )}
                  </Button>
                </div>

                <div
                  className="d-flex justify-content-end"
                  style={{ flex: "3 1 250px", minWidth: "250px" }}
                >
                  {paginationBar("top")}
                </div>
              </div>

              {/* ── Filter panel ─────────────────────────────────────────── */}
              <Collapse in={showFilters}>
                <div>
                  <div className="border rounded p-3 mb-3 bg-body-secondary">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="fw-semibold text-muted small text-uppercase">Filtres avancés</span>
                      {activeFilterCount > 0 && (
                        <button className="btn btn-danger btn-icon-split btn-sm" onClick={resetFilters}>
                          <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
                            <FaTimes style={{ color: "#fff" }} />
                          </span>
                          <span className="text">Réinitialiser</span>
                        </button>
                      )}
                    </div>

                    {/* Statut */}
                    <div className="mb-3">
                      <Form.Label className="small fw-semibold mb-1">Statut</Form.Label>
                      <div className="d-flex gap-3 flex-wrap">
                        {ACTIVE_FILTER_STATUSES.map(s => (
                          <Form.Check
                            key={s}
                            type="checkbox"
                            id={`tkt-status-${s}`}
                            checked={filterStatuses.has(s)}
                            onChange={() => toggleStatus(s)}
                            label={
                              <Badge bg={STATUS_BADGE[s]} text={s === "in_progress" ? "dark" : undefined}>
                                {STATUS_LABELS[s]}
                              </Badge>
                            }
                          />
                        ))}
                      </div>
                    </div>

                    {/* Client + Description */}
                    <Row className="mb-3 g-3">
                      <Col md={6}>
                        <Form.Label className="small fw-semibold mb-1">Client</Form.Label>
                        <Select
                          isClearable
                          value={filterClient}
                          onChange={opt => { setFilterClient(opt); setPage(1); }}
                          options={clientOptions}
                          onInputChange={val => setFilterClientInput(val)}
                          inputValue={filterClientInput}
                          placeholder="Rechercher un client..."
                          noOptionsMessage={() => filterClientInput.length < 3 ? "Entrez au moins 3 caractères" : "Aucun client trouvé"}
                          styles={customSelectStyles}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold mb-1">Description</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Filtrer par description..."
                          value={filterDescription}
                          onChange={e => { setFilterDescription(e.target.value); setPage(1); }}
                        />
                      </Col>
                    </Row>

                    {/* Type + Priorité + Facturation */}
                    <Row className="mb-3 g-3">
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Type</Form.Label>
                        <Select isClearable value={filterType} onChange={opt => { setFilterType(opt); setPage(1); }} options={typeOptions} placeholder="Tous les types" styles={customSelectStyles} />
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Priorité</Form.Label>
                        <Select isClearable value={filterPriorite} onChange={opt => { setFilterPriorite(opt); setPage(1); }} options={prioriteOptions} placeholder="Toutes les priorités" styles={customSelectStyles} />
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Facturation</Form.Label>
                        <Select isMulti isClearable value={filterFacturation} onChange={opt => { setFilterFacturation(opt || []); setPage(1); }} options={factOptions} placeholder="Toutes les méthodes" styles={customSelectStyles} />
                      </Col>
                    </Row>

                    {/* Utilisateur / Groupe assigne */}
                    <Row className="mb-3 g-3">
                      <Col md={6}>
                        <Form.Label className="small fw-semibold mb-1">Utilisateur assigne</Form.Label>
                        <Select isMulti isClearable value={filterAssignedUsers} onChange={opt => { setFilterAssignedUsers(opt || []); setPage(1); }} options={userOptions} placeholder="Tous les utilisateurs" styles={customSelectStyles} />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold mb-1">Groupe assigne</Form.Label>
                        <Select isMulti isClearable value={filterAssignedGroups} onChange={opt => { setFilterAssignedGroups(opt || []); setPage(1); }} options={groupOptions} placeholder="Tous les groupes" styles={customSelectStyles} />
                      </Col>
                    </Row>

                    {/* Dates */}
                    <Row className="g-3">
                      {[
                        { label: "Date de début",   state: filterDateDebut,   setState: setFilterDateDebut,   defaultOp: ">=" },
                        { label: "Date de fin",     state: filterDateFin,     setState: setFilterDateFin,     defaultOp: "<=" },
                        { label: "Date de clôture", state: filterDateCloture, setState: setFilterDateCloture, defaultOp: "="  },
                      ].map(({ label, state, setState, defaultOp }) => (
                        <Col md={4} key={label}>
                          <Form.Label className="small fw-semibold mb-1">{label}</Form.Label>
                          <InputGroup>
                            <Form.Select value={state.op} onChange={e => setState(prev => ({ ...prev, op: e.target.value }))} style={{ maxWidth: "60px" }}>
                              {OP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Form.Select>
                            <Form.Control type="date" value={state.val} onChange={e => { setState(prev => ({ ...prev, val: e.target.value })); setPage(1); }} />
                            {state.val && (
                              <Button variant="outline-secondary" onClick={() => setState({ op: defaultOp, val: "" })}>
                                <FaTimes size={10} />
                              </Button>
                            )}
                          </InputGroup>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </div>
              </Collapse>

              {/* Content area */}
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status" style={{ color: "#f6c23e" }}>
                    <span className="visually-hidden">Chargement...</span>
                  </Spinner>
                </div>

              ) : isError ? (
                <div className="alert alert-danger">
                  Erreur lors du chargement des tickets
                </div>

              ) : (
                <>
                  {/* Mobile: card grid / Desktop: table */}
                  {isMobile ? (
                    <div className="row g-3">
                      {formattedTickets.length === 0 ? (
                        <div className="col-12">
                          <div className="alert alert-info text-center mb-0">Aucun ticket trouvé</div>
                        </div>
                      ) : (
                        formattedTickets.map((ticket, idx) => (
                          <div key={ticket.id ?? `ticket-${idx}`} className="col-12">
                            <TicketRow ticket={ticket} listState={{ page, limit, search, showFilters, filterStatuses: [...filterStatuses], filterClient, filterClientInput, filterDescription, filterType, filterPriorite, filterFacturation, filterAssignedUsers, filterAssignedGroups, filterDateDebut, filterDateFin, filterDateCloture }} asCard />
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <Table striped hover responsive className="align-middle">
                      <thead>
                        <tr>
                          {[
                            { label: "ID",         field: "id" },
                            { label: "Titre",      field: "titre" },
                            { label: "Date début", field: "date_debut" },
                            { label: "Client",     field: "client" },
                          ].map(({ label, field }) => (
                            <th
                              key={field}
                              onClick={() => handleSort(field)}
                              style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                            >
                              {label}{" "}
                              {sortField === field
                                ? (sortOrder === "ASC" ? "▲" : "▼")
                                : <span style={{ opacity: 0.25 }}>▲</span>}
                            </th>
                          ))}
                          <th>Intervenant(s)</th>
                          <th>Intervention(s)</th>
                          <th>Statut</th>
                          <th>Action(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formattedTickets.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center">Aucun ticket trouvé</td>
                          </tr>
                        ) : (
                          formattedTickets.map((ticket, idx) => (
                            <TicketRow
                              key={ticket.id ?? `ticket-${idx}`}
                              ticket={ticket}
                              listState={{ page, limit, search, showFilters, filterStatuses: [...filterStatuses], filterClient, filterClientInput, filterDescription, filterType, filterPriorite, filterFacturation, filterAssignedUsers, filterAssignedGroups, filterDateDebut, filterDateFin, filterDateCloture }}
                            />
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}

                  {/* Bottom pagination + total count */}
                  <div className="d-flex flex-column gap-3 mt-3">
                    {paginationBar("bottom")}
                    <div className="text-muted fst-italic">Total : {totalItems} tickets</div>
                  </div>
                </>
              )}

            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Tickets;